import React, { useMemo } from 'react'
import DateVO from '../../../../../../../../../vo/DateVO'
import { GanttCellRendererProps } from './index'
import { GanttColor } from '../../../../../../../../../styles/commonStyles'
import { WbsItemStatus } from '../../wbsItemStatus'
import objects from '../../../../../../../../../utils/objects'
import { GanttDisplayUnit } from '../../../../../../../../../domain/value-object/GanttParameterVO'
import { CalendarDateVO } from '../../../../../../../../../domain/value-object/CalendarDateVO'
import { colorPalette } from '../../../../../../../../style/colorPalette'
import { MilestoneDetail } from '../../../../../../../../../domain/entity/MilestoneEntity'

const Colors = {
  GRID_LINE: '#d8dde5',
  GRID_LINE_LIGHT: colorPalette.monotone[2] + '66',
  TODAY_BACKGROUND: '#f9e8d2',
  TODAY_BORDER: GanttColor.BORDER_TODAY,
  HOLIDAY_BACKGROUND: '#d8dde526',
  WARNING_BACKGROUND: '#fffacd',
  SCHEDULED: '#5bb9ff',
  SCHEDULED_BORDER: '#5bb9ff',
  LATE: '#f486b0',
  LATE_BORDER: '#f486b0',
  DONE: '#f7f8fa',
  DONE_BORDER: '#d8dde5',
} as const

interface DatePosition {
  x: number
  width: number
  date: CalendarDateVO
}

interface Props extends GanttCellRendererProps {}

export const GanttBackgroundCanvas = React.memo((props: Props) => {
  const { data, dataPath, timeScale, parameter, displayTimeScale, milestones } =
    props
  const prefix = dataPath ? `${dataPath}.` : ''
  const wbsItem = objects.getValue(data, `${prefix}wbsItem`)
  const cumulation = objects.getValue(data, `${prefix}cumulation`)

  const status = wbsItem?.status
  const scheduledDate = wbsItem?.scheduledDate
  const actualDate = wbsItem?.actualDate
  const hasValidData = !!(status && scheduledDate && actualDate)

  if (!hasValidData) {
    return <div className="sevend-ag-cell-gantt-chart" />
  }

  return (
    <GanttChart
      status={status}
      scheduledDate={scheduledDate}
      actualDate={actualDate}
      cumulation={cumulation}
      timeScale={timeScale}
      displayTimeScale={displayTimeScale}
      unit={parameter.unit}
      milestones={milestones}
    />
  )
})

GanttBackgroundCanvas.displayName = 'GanttBackgroundCanvas'

interface GanttChartProps {
  status: WbsItemStatus
  scheduledDate: { startDate?: Date; endDate?: Date }
  actualDate: { startDate?: Date; endDate?: Date }
  cumulation?: { maxScheduledEndDate?: Date }
  timeScale: CalendarDateVO[]
  displayTimeScale: CalendarDateVO[]
  unit: GanttDisplayUnit
  milestones?: MilestoneDetail[]
}

const GanttChart = React.memo((props: GanttChartProps) => {
  const {
    status,
    scheduledDate,
    actualDate,
    cumulation,
    timeScale,
    displayTimeScale,
    unit,
    milestones,
  } = props

  const datePositions = useDatePositions(timeScale, unit)

  const dateInfo = useMemo(() => {
    const now = new Date()
    return {
      wbsIsDoneOrDiscard: [WbsItemStatus.DONE, WbsItemStatus.DISCARD].includes(
        status
      ),
      wbsIsReview: status === WbsItemStatus.REVIEW,
      scheduledStart: scheduledDate.startDate
        ? CalendarDateVO.of(scheduledDate.startDate)
        : undefined,
      scheduledEnd: scheduledDate.endDate
        ? CalendarDateVO.of(scheduledDate.endDate)
        : undefined,
      actualStart: actualDate.startDate
        ? CalendarDateVO.of(actualDate.startDate)
        : undefined,
      actualEnd: actualDate.endDate
        ? CalendarDateVO.of(actualDate.endDate)
        : undefined,
      today: CalendarDateVO.of(now),
      endOfToday: CalendarDateVO.of(new DateVO(now).addDays(1).toDate()),
      maxScheduledDate: cumulation?.maxScheduledEndDate
        ? CalendarDateVO.of(cumulation.maxScheduledEndDate)
        : undefined,
    }
  }, [status, scheduledDate, actualDate, cumulation])

  const backgrounds = useBackgrounds(datePositions, dateInfo, unit)
  const gridLines = useGridLines(datePositions, unit, displayTimeScale)
  const bars = useBars(datePositions, dateInfo)
  const milestoneLines = useMilestoneLines(datePositions, milestones)

  return (
    <div
      className="sevend-ag-cell-gantt-chart"
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
        preserveAspectRatio="none"
      >
        <g className="gantt-backgrounds">{backgrounds}</g>
        <g className="gantt-grid-lines">{gridLines}</g>
        <g className="gantt-bars">{bars}</g>
        <g className="gantt-milestones">{milestoneLines}</g>
      </svg>
    </div>
  )
})

GanttChart.displayName = 'GanttChart'

// Custom hooks

const useDatePositions = (
  timeScale: CalendarDateVO[],
  unit: GanttDisplayUnit
): DatePosition[] => {
  return useMemo(() => {
    const isMonthUnit = unit === GanttDisplayUnit.MONTH

    if (!isMonthUnit) {
      const width = 100 / timeScale.length
      return timeScale.map((date, i) => ({ x: i * width, width, date }))
    }

    const monthDaysCache = new Map<string, number>()
    const getMonthDays = (date: CalendarDateVO): number => {
      const key = `${date.date.getFullYear()}-${date.date.getMonth()}`
      if (!monthDaysCache.has(key)) {
        monthDaysCache.set(
          key,
          new Date(
            date.date.getFullYear(),
            date.date.getMonth() + 1,
            0
          ).getDate()
        )
      }
      return monthDaysCache.get(key)!
    }

    const uniqueMonths = new Set(
      timeScale.map(d => `${d.date.getFullYear()}-${d.date.getMonth()}`)
    )
    const monthWidth = 100 / uniqueMonths.size

    const positions: DatePosition[] = []
    let currentX = 0
    for (const date of timeScale) {
      const width = monthWidth / getMonthDays(date)
      positions.push({ x: currentX, width, date })
      currentX += width
    }
    return positions
  }, [timeScale, unit])
}

const useBackgrounds = (
  datePositions: DatePosition[],
  dateInfo: {
    today: CalendarDateVO
    scheduledEnd?: CalendarDateVO
    maxScheduledDate?: CalendarDateVO
  },
  unit: GanttDisplayUnit
): React.ReactNode[] => {
  return useMemo(() => {
    const { today, scheduledEnd, maxScheduledDate } = dateInfo
    const backgrounds: React.ReactNode[] = []
    const barY = 25
    const barHeight = 50

    for (let i = 0; i < datePositions.length; i++) {
      const { x, width, date } = datePositions[i]
      const isToday = date.isEqual(today)

      if (isToday) {
        backgrounds.push(
          <rect
            key={`today-${i}`}
            x={`${x}%`}
            y="0"
            width={`${width}%`}
            height="100%"
            fill={Colors.TODAY_BACKGROUND}
          />
        )
      } else if (unit === GanttDisplayUnit.DAY && date.isHoliday()) {
        backgrounds.push(
          <rect
            key={`holiday-${i}`}
            x={`${x}%`}
            y="0"
            width={`${width}%`}
            height="100%"
            fill={Colors.HOLIDAY_BACKGROUND}
          />
        )
      }

      if (
        scheduledEnd &&
        maxScheduledDate &&
        date.isAfter(scheduledEnd) &&
        !date.isAfter(maxScheduledDate)
      ) {
        backgrounds.push(
          <rect
            key={`warning-${i}`}
            x={`${x}%`}
            y={`${barY}%`}
            width={`${width}%`}
            height={`${barHeight}%`}
            fill={Colors.WARNING_BACKGROUND}
          />
        )
      }
    }
    return backgrounds
  }, [datePositions, dateInfo, unit])
}

const useGridLines = (
  datePositions: DatePosition[],
  unit: GanttDisplayUnit,
  displayTimeScale: CalendarDateVO[]
): React.ReactNode[] => {
  return useMemo(() => {
    const isDayUnit = unit === GanttDisplayUnit.DAY
    const isMonthUnit = unit === GanttDisplayUnit.MONTH
    const isWeekUnit = unit === GanttDisplayUnit.WEEK

    const gridLines: React.ReactNode[] = []

    for (let i = 1; i < datePositions.length; i++) {
      const { x, date } = datePositions[i]
      const matchScale =
        isDayUnit || displayTimeScale.some(v => v.isEqual(date))

      if (!matchScale) continue

      const isFirstday = !isMonthUnit && date.isFirstday()
      const isFirstmonth = isMonthUnit && date.isFirstmonth()
      const isFirstweek = isWeekUnit && date.isFirstweek()
      const isStrong = isFirstday || isFirstmonth || isFirstweek

      gridLines.push(
        <line
          key={`grid-${i}`}
          x1={`${x}%`}
          y1="0"
          x2={`${x}%`}
          y2="100%"
          stroke={isStrong ? Colors.GRID_LINE : Colors.GRID_LINE_LIGHT}
          strokeWidth="1"
        />
      )
    }
    return gridLines
  }, [datePositions, unit, displayTimeScale])
}

const useBars = (
  datePositions: DatePosition[],
  dateInfo: {
    wbsIsDoneOrDiscard: boolean
    wbsIsReview: boolean
    scheduledStart?: CalendarDateVO
    scheduledEnd?: CalendarDateVO
    actualStart?: CalendarDateVO
    actualEnd?: CalendarDateVO
    endOfToday: CalendarDateVO
  }
): React.ReactNode[] => {
  return useMemo(() => {
    const {
      wbsIsDoneOrDiscard,
      wbsIsReview,
      scheduledStart,
      scheduledEnd,
      actualStart,
      actualEnd,
      endOfToday,
    } = dateInfo

    const bars: React.ReactNode[] = []
    const barY = 25
    const barHeight = 50

    const findPosition = (targetDate: CalendarDateVO) =>
      datePositions.find(p => p.date.isEqual(targetDate))

    // Late bar
    if (!actualStart && !actualEnd && scheduledStart?.isBefore(endOfToday)) {
      const startPos = findPosition(scheduledStart)
      const endPos = findPosition(endOfToday)
      if (startPos && endPos && endPos.x > startPos.x) {
        bars.push(
          <rect
            key="late-bar"
            x={`${startPos.x}%`}
            y={`${barY}%`}
            width={`${endPos.x - startPos.x}%`}
            height={`${barHeight}%`}
            fill={Colors.LATE}
            stroke={Colors.LATE_BORDER}
            strokeWidth={2}
            rx={4}
          />
        )
      }
    }

    // Actual result bar
    if (actualStart) {
      const startPos = findPosition(actualStart)
      if (startPos) {
        const displayEnd = actualEnd || endOfToday
        const endPos = findPosition(displayEnd)
        const barEndX = endPos
          ? actualEnd
            ? endPos.x + endPos.width
            : endPos.x
          : 100
        const barWidth = barEndX - startPos.x

        if (barWidth > 0) {
          const isLate = scheduledEnd && displayEnd.isAfter(scheduledEnd)
          const isDone = wbsIsDoneOrDiscard || (wbsIsReview && actualEnd)

          const fillColor = isDone
            ? Colors.DONE
            : isLate
              ? Colors.LATE
              : Colors.SCHEDULED
          const borderColor = isDone
            ? Colors.DONE_BORDER
            : isLate
              ? Colors.LATE_BORDER
              : Colors.SCHEDULED_BORDER

          bars.push(
            <rect
              key="actual-bar"
              x={`${startPos.x}%`}
              y={`${barY}%`}
              width={`${barWidth}%`}
              height={`${barHeight}%`}
              fill={fillColor}
              stroke={borderColor}
              strokeWidth={2}
              rx={4}
            />
          )
        }
      }
    }

    // Today line
    const todayPos = findPosition(endOfToday)
    if (todayPos) {
      const todayX = todayPos.x - todayPos.width / 2
      bars.push(
        <line
          key="today-line"
          x1={`${todayX}%`}
          y1="0"
          x2={`${todayX}%`}
          y2="100%"
          stroke={Colors.TODAY_BORDER}
          strokeWidth="2"
        />
      )
    }

    return bars
  }, [datePositions, dateInfo])
}

const useMilestoneLines = (
  datePositions: DatePosition[],
  milestones?: MilestoneDetail[]
): React.ReactNode[] => {
  return useMemo(() => {
    if (!milestones || milestones.length === 0) return []

    const lines: React.ReactNode[] = []

    const findPosition = (targetDate: CalendarDateVO) =>
      datePositions.find(p => p.date.isEqual(targetDate))

    const groupedByDate = new Map<string, MilestoneDetail[]>()
    for (const milestone of milestones) {
      const dateKey = milestone.milestoneDate
      if (!groupedByDate.has(dateKey)) {
        groupedByDate.set(dateKey, [])
      }
      groupedByDate.get(dateKey)!.push(milestone)
    }

    for (const [dateKey, groupMilestones] of groupedByDate) {
      const milestoneDate = CalendarDateVO.of(new Date(dateKey))
      const pos = findPosition(milestoneDate)

      if (pos) {
        const firstMilestone = groupMilestones[0]
        const color = firstMilestone.color
        const xPos = pos.x + pos.width / 2
        const allAchieved = groupMilestones.every(
          milestone => milestone.achieved
        )
        const opacity = allAchieved ? 0.4 : 1

        lines.push(
          <line
            key={`milestone-${dateKey}`}
            x1={`${xPos}%`}
            y1="0"
            x2={`${xPos}%`}
            y2="100%"
            stroke={color}
            strokeWidth="2"
            strokeOpacity={opacity}
          />
        )
      }
    }

    return lines
  }, [datePositions, milestones])
}
